/* eslint-disable react-hooks/exhaustive-deps */
import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { BsChevronDown, BsQuestionSquare } from "react-icons/bs";
import { KeyValueObj } from "../../types";

import { SelectWrapper } from "./style";
import CustomTooltip from "../Tooltip";

export interface ISelectProps {
  options: any[];
  placeholder: string;
  prompt: string;
  fullWidth: boolean;
  shortWidth: boolean;
  name: string;
  indexLevel: number;
  returnId?: boolean;
  valueType?: string;
  initialValue?: string;
  tooltip?: string;
  updateValidity?: (name: string, validity: boolean) => void;
  updateData?: (
    name: string,
    value: { id: string | number; title: string } | string,
  ) => void;
  onEveryChange?: (
    latestInput: string,
    name: string,
    updateValidity?: (name: string, validity: boolean) => void,
    setFormValues?: Dispatch<SetStateAction<KeyValueObj>>,
    fomrValues?: KeyValueObj,
  ) => void;
  formValues: KeyValueObj;
  setFormValues: Dispatch<SetStateAction<KeyValueObj>>;
  handleSearch?: (value: string) => void;
  required?: boolean;
  dataTestId?: string;
  readOnly?: boolean;
}

export function Select({
  options,
  placeholder,
  prompt,
  fullWidth,
  updateValidity,
  updateData,
  name,
  indexLevel,
  returnId,
  shortWidth,
  valueType,
  initialValue,
  onEveryChange,
  formValues,
  setFormValues,
  tooltip,
  handleSearch,
  required,
  dataTestId,
  readOnly = false,
}: ISelectProps) {
  const [selectedValue, setSelectedValue] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [selectOptions, setSelectOptions] = useState<KeyValueObj[]>([]);
  const [initialFiltered] = useState<KeyValueObj[]>([]);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Sync with parent formValues when returning to this step
    if (formValues && formValues[name]) {
      const currentValue = formValues[name];

      // MED-656: ATC selections always store the row id (see onSelectOption
      // below), same as the returnId case, but formatted as "code - title"
      // rather than a plain title. Without this branch the id set by
      // onSelectOption's initial setSelectedValue got clobbered by this
      // effect re-running on the formValues update it just caused, leaving
      // the closed input showing the raw id instead of the ATC code.
      if (valueType === "ATC" && options.length > 0) {
        const item = options.find(
          (option: KeyValueObj) => option.id === currentValue,
        );
        if (item)
          setSelectedValue(`${item.code_identifier} - ${item.code_title}`);
      } else if (returnId && options.length > 0) {
        const item = options.find(
          (option: KeyValueObj) => option.id === currentValue,
        );
        if (item) setSelectedValue(item.title);
      } else {
        setSelectedValue(currentValue);
      }
    }
  }, [formValues, name, options, valueType]);

  useEffect(() => {
    // Prefill flows (e.g. editing a product) pass an `initialValue` but leave
    // the parent form state empty. Seed it once into the form so the value both
    // displays (handled by the sync effect above) and is included in the submit
    // payload — mirroring how the text Input consumes `initialValue`.
    const hasInitial =
      initialValue !== undefined && initialValue !== null && initialValue !== "";
    if (hasInitial && !(formValues && formValues[name])) {
      updateData?.(name, initialValue);
      updateValidity?.(name, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  function onSelectClicked() {
    if (readOnly) return;
    setSelectOptions(options);
    setFadeOut(false);
    setShowOptions(!showOptions);
    setClicked(true);
  }

  const onSelectOption = (item: any) => {
    if (readOnly) return;
    updateValidity?.(name, true);

    if (valueType === "ATC") {
      setSelectedValue(`${item.code_identifier} - ${item.code_title}`);
    } else {
      setSelectedValue(item.title);
    }
    removeOptions();

    if (returnId || valueType === "ATC") {
      updateData?.(name, item.id);
      onEveryChange &&
        onEveryChange(item.id, name, updateValidity, setFormValues, formValues);
    } else {
      updateData?.(name, item.title);
      onEveryChange &&
        onEveryChange(
          item.title,
          name,
          updateValidity,
          setFormValues,
          formValues,
        );
    }

    updateValidity?.(name, true);
  };

  function onInputBlur() {
    removeOptions();
  }

  function removeOptions() {
    setFadeOut(true);
    window.setTimeout(() => {
      setShowOptions(false);
    }, 350);
  }

  function onInputChange(e: FormEvent<HTMLInputElement>) {
    const { value } = e.currentTarget;
    if (handleSearch) {
      return handleSearch(value);
    }
    if (valueType === "ATC") {
      if (value.length > 2) {
        const filtered = options.filter(
          (option: KeyValueObj) =>
            option.code_title?.toLowerCase()?.includes(value.toLowerCase()) ||
            option.code_identifier
              ?.toLowerCase()
              ?.includes(value.toLowerCase()),
        );
        setSelectOptions(filtered);
      } else {
        setSelectOptions(initialFiltered);
      }
    } else {
      const filtered = options.filter(
        (option: KeyValueObj) =>
          option?.title?.toLowerCase().includes(value.toLowerCase()) ||
          option?.id?.toLowerCase().includes(value.toLowerCase()),
      );
      setSelectOptions(filtered);
    }
  }

  return (
    <SelectWrapper
      shortWidth={shortWidth}
      indexLevel={indexLevel}
      showOptions={showOptions}
      fullWidth={fullWidth}
      fadeOut={fadeOut}
    >
      <div className="label-wrapper">
        <label>
          {prompt} {required ? <span style={{ color: "red" }}>*</span> : null}
        </label>
        {tooltip ? (
          <CustomTooltip title={tooltip}>
            <div>
              <BsQuestionSquare className="tooltip-icon" />
            </div>
          </CustomTooltip>
        ) : null}
      </div>
      <div className="select-wrapper">
        <div className="select-input">
          {showOptions && !readOnly ? (
            <input
              onChange={onInputChange}
              onBlur={onInputBlur}
              autoFocus
              type="text"
            />
          ) : selectedValue ? (
            <h3 onClick={onSelectClicked}>{selectedValue}</h3>
          ) : (
            <h4 id={name} data-testid={dataTestId} onClick={onSelectClicked}>
              {placeholder}
            </h4>
          )}
          {/* hide chevron down if readOnly */}
          {!readOnly && <BsChevronDown className="icon" />}
        </div>
        {clicked && !showOptions && !selectedValue && !readOnly && (
          <h3 className="error-message">Please select from the options</h3>
        )}
        {showOptions && !readOnly && (
          <ul className={`select-options`}>
            {valueType === "ATC"
              ? selectOptions &&
                selectOptions.map((item, i) => {
                  return (
                    <li
                      onClick={() => onSelectOption(item)}
                      key={i}
                      value={item.title}
                      id={`${name}-${i}`}
                    >
                      {item.code_identifier} - {item.code_title}
                    </li>
                  );
                })
              : selectOptions &&
                selectOptions.map((item, i) => {
                  return (
                    <li
                      onClick={() => onSelectOption(item)}
                      key={i}
                      value={item.title}
                      id={`${name}-${i}`}
                    >
                      {item.title}
                    </li>
                  );
                })}
          </ul>
        )}
      </div>
    </SelectWrapper>
  );
}
